import React, { useState, useEffect } from "react";
import { Alert, Button, Card, Col, Row } from "react-bootstrap";
import {
  createJobPostingCheckoutSession,
  checkUserJobPaymentHistory,
} from "../../../Services/StripeService";
import { getUserDetails } from "../../../Services/GetFields";
import { getTotalSTMCount } from "../../../Services/AdminManageUsersServices";
import { FREE_JOB_POSTING_STM_THRESHOLD } from "../../../Utilities/Helper";

export default function PaymentWall({ onPaymentVerified }) {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        setLoading(true);

        // Check if we're in the free posting period (< 200 STMs)
        try {
          const totalSTMCount = await getTotalSTMCount();
          if (totalSTMCount < FREE_JOB_POSTING_STM_THRESHOLD) {
            onPaymentVerified(true);
            setLoading(false);
            return;
          }
        } catch (stmErr) {
          console.error("Error checking STM count, proceeding with normal payment flow:", stmErr);
        }

        // Get user details
        const userResponse = await getUserDetails();
        const user = userResponse.response;
        setUserData(user);

        // Check localStorage for recent payment
        const storedPayment = localStorage.getItem(
          "jobPostingPaymentCompleted"
        );
        if (storedPayment) {
          const paymentData = JSON.parse(storedPayment);
          const paymentTime = new Date(paymentData.timestamp);
          const now = new Date();
          const diffInMinutes = (now - paymentTime) / (1000 * 60);

          // If payment was completed within the last 30 minutes, allow access
          if (diffInMinutes < 30 && paymentData.userId === user.id.toString()) {
            onPaymentVerified(true);
            setLoading(false);
            return;
          }
        }

        // Check backend for recent payments
        const paymentHistory = await checkUserJobPaymentHistory(user.id);
        if (paymentHistory.success && paymentHistory.hasRecentPayment) {
          onPaymentVerified(true);
          setLoading(false);
          return;
        }

        // No valid payment found
        onPaymentVerified(false);
        setLoading(false);
      } catch (err) {
        console.error("Error checking payment status:", err);
        setError("Failed to verify payment status. Please try again.");
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [onPaymentVerified]);

  const handlePayment = async () => {
    try {
      setProcessing(true);
      setError(null);

      if (!userData) {
        throw new Error("User data not available");
      }

      const result = await createJobPostingCheckoutSession(
        userData.id.toString(),
        userData.email
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to create checkout session");
      }

      // The function will redirect to Stripe, so we shouldn't reach here
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError(err.message || "Failed to start payment process");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="container p-0">
        <div className="dashboard-wrapper">
          <Card
            className="mx-auto shadow border-0"
            style={{
              maxWidth: "600px",
              borderRadius: "1rem",
            }}
          >
            <Card.Header
              as="h4"
              className="text-white py-4 text-center"
              style={{
                background: "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
                borderTopLeftRadius: "1rem",
                borderTopRightRadius: "1rem",
              }}
            >
              Post a Job
            </Card.Header>
            <Card.Body className="text-center px-md-5 py-5">
              <div className="mb-4">
                <div
                  className="spinner-border text-primary"
                  style={{
                    width: "4rem",
                    height: "4rem",
                    color: "#2660a5 !important",
                  }}
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
              <p className="mt-3 text-muted">Checking payment status...</p>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container p-0">
      <div className="dashboard-wrapper mt-4">
        <Card
          className="mx-auto shadow border-0"
          style={{
            maxWidth: "700px",
            borderRadius: "1rem",
          }}
        >
          <Card.Header
            as="h4"
            className="text-white py-4 text-center"
            style={{
              background: "linear-gradient(135deg, #2660a5 0%, #4a8eda 100%)",
              borderTopLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
            }}
          >
            Job Posting
          </Card.Header>
          <Card.Body className="px-md-5 py-5">
            <div className="text-center mb-4">
              <p className="text-muted lead">
                Unlock access to top talent on JobInfoNetwork
              </p>
            </div>

            <Row className="mb-4">
              <Col md={12}>
                <div
                  className="p-4 rounded"
                  style={{
                    background: "linear-gradient(to right, #f8f9fa, #e9ecef)",
                    borderLeft: "4px solid #2660a5",
                  }}
                >
                  <h4 className="mb-4 font-weight-bold">Job Posting</h4>

                  <div className="row mb-3">
                    <div className="col-8">
                      <span style={{ fontSize: "18px" }}>
                        Fee Per Job Posting /month
                      </span>
                    </div>
                    <div className="col-4 text-right">
                      <strong style={{ fontSize: "18px" }}>$50.00</strong>
                    </div>
                  </div>

                  <hr style={{ borderColor: "#dee2e6", margin: "1rem 0" }} />

                  <div className="row mb-4">
                    <div className="col-8">
                      <strong style={{ fontSize: "20px" }}>Total</strong>
                    </div>
                    <div className="col-4 text-right">
                      <strong style={{ fontSize: "20px", color: "#2660a5" }}>
                        $50.00
                      </strong>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h6 className="font-weight-bold mb-3">
                      Each Job Posting Includes
                    </h6>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-check-circle text-success mr-2"></i>
                      <span>
                        Exposure to a network of qualified professionals
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-check-circle text-success mr-2"></i>
                      <span>Easy resume management tools</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-check-circle text-success mr-2"></i>
                      <span>
                        Direct messaging with interested candidates
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {error && (
              <Alert
                variant="danger"
                className="mb-4 border-0 shadow-sm"
                style={{ borderRadius: "0.5rem" }}
              >
                {error}
              </Alert>
            )}

            <div className="text-center">
              <Button
                variant="primary"
                size="lg"
                onClick={handlePayment}
                disabled={processing}
                className="px-5 py-3"
                style={{
                  background: processing
                    ? "linear-gradient(135deg, #6c757d 0%, #adb5bd 100%)"
                    : "linear-gradient(135deg, #ffc107 0%, #ffcd39 100%)",
                  borderRadius: "2rem",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.12)",
                  border: "none",
                  fontSize: "18px",
                  fontWeight: "600",
                }}
              >
                {processing ? (
                  <>
                    <div
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      style={{ width: "1.2rem", height: "1.2rem" }}
                    >
                      <span className="sr-only">Loading...</span>
                    </div>
                    Processing...
                  </>
                ) : (
                  "Create Job Posting"
                )}
              </Button>

              <div className="mt-4">
                <div className="d-flex justify-content-center align-items-center">
                  Start reaching qualified candidates today!
                </div>
              </div>
            </div>
          </Card.Body>
          <Card.Footer
            className="text-muted text-center py-4"
            style={{
              borderBottomLeftRadius: "1rem",
              borderBottomRightRadius: "1rem",
            }}
          >
            <i className="fas fa-lock text-muted mr-2"></i>
            <small className="text-muted">
              Secure payment powered by Stripe
            </small>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
}
